import { useCallback, useEffect, useState } from 'react'
import { getApi } from '../../api/api'
import { useDispatch, useSelector } from 'react-redux'
import { selectDarkTheme, selectSoundEnabled } from '../../store/slices/settingsSlice'
import { selectModalState, setCommonModalState } from '../../store/slices/commonModalSlice'
import { RootState } from '../../store/store'
import {
  AfterActions,
  CommonModals,
  NotificationTypes,
  OrderStatus,
} from '../../common/types/enums'
import { useRefresh } from '../../common/hooks/useRefresh'
import { selectAuthToken } from '../../store/slices/authSlice'
import { getWs } from '../../ws/ws'
import { useSearchParams } from 'react-router-dom'
import { selectCurrentUser } from '../../store/slices/currentUserSlice'
import { selectChatModalActive, setChatModalState } from '../../store/slices/chatSlice'
import { selectMapSliderVisible, toggleMapSlider } from '../../store/slices/gMapSlice'
import { getSalesApi } from '../../salesApi/salesApi'
import {
  resetCurrentCluster,
  selectCurrentCluster,
  setCurrentCluster,
} from '../../store/slices/currentClusterSlice'
import useInterval from '@use-it/interval'
import { selectOrderList, setOrderList } from '../../store/slices/orderListSlice'
import { addNotification } from '../../store/slices/notificationSlice'
import { v4 } from 'uuid'
import { format } from 'date-fns'
import sound from '../../assets/sounds/sound5.mp3'
import { checkOrderDelay } from '../../common/utils/constants'
import { usePlaySound } from '../../common/hooks/usePlaySound'
import { setClusterList } from '../../store/slices/clusterListSlice'
import { openPayment } from '../../store/slices/paymentSlice'

export const useAppWrapper = () => {
  const [api] = useState(() => getApi())
  const [salesApi] = useState(() => getSalesApi())
  const dispatch = useDispatch()
  const darkTheme = useSelector(selectDarkTheme)
  const token = useSelector(selectAuthToken)
  const currentUser = useSelector(selectCurrentUser)
  const [ws] = useState(getWs())
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const mapVisible = useSelector(selectMapSliderVisible)
  const [scrollPosition, setScrollPosition] = useState<number>(0)
  const [upVisible, setUpVisible] = useState(false)
  const chatActive = useSelector(selectChatModalActive)
  const currentCluster = useSelector(selectCurrentCluster)
  const orderList = useSelector(selectOrderList)
  const [attachEmailMode, setAttachEmailMode] = useState(false)

  const { playSound } = usePlaySound()

  const { refresh } = useRefresh()

  const loginModalActive = useSelector((state: RootState) =>
    selectModalState(state, CommonModals.LOGIN),
  )
  const setLoginModalState = (state: boolean) =>
    dispatch(setCommonModalState({ modal: CommonModals.LOGIN, state }))
  const paymentModalActive = useSelector((state: RootState) =>
    selectModalState(state, CommonModals.PAYMENT),
  )
  const setPaymentModalState = (state: boolean) =>
    dispatch(setCommonModalState({ modal: CommonModals.PAYMENT, state }))

  const statusMenuActive = useSelector((state: RootState) =>
    selectModalState(state, CommonModals.STATUS_MENU),
  )
  const chatMenuActive = useSelector((state: RootState) =>
    selectModalState(state, CommonModals.CHAT_MENU),
  )
  const filterActive = useSelector((state: RootState) =>
    selectModalState(state, CommonModals.FILTER),
  )
  const sortActive = useSelector((state: RootState) => selectModalState(state, CommonModals.SORT))

  const setStatusMenuState = (state: boolean) =>
    dispatch(setCommonModalState({ modal: CommonModals.STATUS_MENU, state }))

  const setChatMenuState = useCallback(
    (state: boolean) => dispatch(setCommonModalState({ modal: CommonModals.CHAT_MENU, state })),
    [dispatch],
  )

  const setSortState = (state: boolean) =>
    dispatch(setCommonModalState({ modal: CommonModals.SORT, state }))

  const setFilterState = (state: boolean) =>
    dispatch(setCommonModalState({ modal: CommonModals.FILTER, state }))

  const closeStatusMenu = () => {
    if (statusMenuActive) setStatusMenuState(false)
  }
  const closeChatMenu = useCallback(() => {
    if (chatMenuActive) setChatMenuState(false)
  }, [chatMenuActive, setChatMenuState])

  const closeFilter = () => {
    if (filterActive) setFilterState(false)
  }
  const closeSort = () => {
    if (sortActive) setSortState(false)
  }
  const closeMap = () => {
    if (mapVisible) dispatch(toggleMapSlider())
  }

  const upAction = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const getClusters = useCallback(async () => {
    try {
      const result = await api.getClusterList()
      if (result?.data.length) {
        dispatch(setClusterList(result.data))

        const current = JSON.parse(localStorage.getItem('currentCluster') || 'null')
        if (current) {
          if (
            result.data.some(
              (el) =>
                el.id === current.id && el.port === current.port && el.token === current.token,
            )
          ) {
            dispatch(setCurrentCluster(current))
            salesApi.setClusterUrl(current)
            return
          }
        }
        dispatch(setCurrentCluster(result.data[0]))
        salesApi.setClusterUrl(result.data[0])
      } else {
        dispatch(resetCurrentCluster())
        dispatch(setClusterList([]))
      }
    } catch (error) {}
  }, [api, salesApi, dispatch])

  const getOrderList = useCallback(async () => {
    try {
      if (currentCluster) {
        const result = await salesApi.getOrderList({ sort: 'id', order: 'DESC' })
        if (!Array.isArray(result)) return

        if (orderList.length === 0 && result.length > 0) {
          const newOrders = result.filter((order) => Number(order.status) === OrderStatus.NEW)
          const getOrderWord = (count: number) => {
            if (count % 10 === 1 && count % 100 !== 11) return 'заказ'
            if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20))
              return 'заказа'
            return 'заказов'
          }

          if (newOrders.length > 0) {
            dispatch(
              addNotification({
                id: v4(),
                type: NotificationTypes.SUCCESS,
                text: `${newOrders.length} ${getOrderWord(newOrders.length)}, в статусе НОВЫЙ в кластере ${currentCluster.name}`,
              }),
            )
            playSound(sound, 1)
          }
        }

        if (orderList.length > 0) {
          const existingIds = new Set(orderList.map((order) => order.id))
          const newOrders = result
            .filter((order) => !existingIds.has(order.id))
            .filter((order) => Number(order.status) === OrderStatus.NEW)

          if (newOrders.length > 0) {
            for (const order of newOrders) {
              if (order.createdAt)
                dispatch(
                  addNotification({
                    id: v4(),
                    type: NotificationTypes.SUCCESS,
                    text: `Заказ #${order.orderNumber || order.id} от ${format(new Date(order.createdAt), 'dd.MM.yyyy HH:mm')} на сумму ${order.total || 0} руб.`,
                  }),
                )
            }
            playSound(sound, 1)
          }
        }

        dispatch(setOrderList(result))
      }
    } catch (error) {}
  }, [salesApi, currentCluster, dispatch, orderList, playSound])

  useInterval(getOrderList, checkOrderDelay)

  useEffect(() => {
    if (currentCluster) {
      getOrderList()
    }
  }, [currentCluster])

  useEffect(() => {
    if (token) {
      ws.authEmit()
    }
  }, [token, ws])

  useEffect(() => {
    if (currentUser) getClusters()
  }, [currentUser, getClusters])

  useEffect(() => {
    refresh()
  }, [api])

  useEffect(() => {
    if (id && !currentUser) {
      dispatch(setLoginModalState(true))
    }
  }, [id])

  // Подхват payment_type из URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const paymentType = params.get('payment_type')
    const paymentClusterId = params.get('id')

    if (paymentType === 'cluster' && paymentClusterId) {
      window.history.replaceState({}, '', window.location.pathname)

      if (!currentUser) {
        localStorage.setItem(
          'afterAction',
          JSON.stringify({ action: AfterActions.PAYMENT, clusterId: Number(paymentClusterId) }),
        )
        setLoginModalState(true)
      } else {
        dispatch(openPayment({ clusterId: Number(paymentClusterId), mode: 'cluster' }))
      }
    }
  }, [currentUser])

  useEffect(() => {
    if (mapVisible) dispatch(setChatModalState(false))
  }, [dispatch, mapVisible])

  useEffect(() => {
    if (chatMenuActive && !chatActive) closeChatMenu()
  }, [chatActive, chatMenuActive, closeChatMenu])

  const handleScroll = () => {
    setScrollPosition(window.scrollY)
  }
  

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    setUpVisible(scrollPosition > 200)
  }, [scrollPosition])

  useEffect(() => {
    if (!loginModalActive) {
      setAttachEmailMode(false)
    }
  }, [loginModalActive])

  return {
    darkTheme,
    loginModalActive,
    setLoginModalState,
    paymentModalActive,
    setPaymentModalState,
    closeStatusMenu,
    closeChatMenu,
    closeFilter,
    closeSort,
    upAction,
    upVisible,
    closeMap,
    attachEmailMode,
    setAttachEmailMode,
    getClusters
  }
}
